import React, { Component, ReactNode } from 'react';
import { Button } from './Button';
import { Logo } from './Logo';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Atualiza o state para que a próxima renderização mostre a UI de fallback
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Você pode registrar o erro em um serviço de logging aqui
    console.error('ErrorBoundary capturou um erro:', error);
    console.error('Informações do erro:', errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Aqui você pode integrar com serviços como Sentry, LogRocket, etc.
    // Ex: Sentry.captureException(error);
  }

  handleReload = () => {
    // Recarrega a página
    window.location.reload();
  };

  handleReset = () => {
    // Reseta o estado do error boundary
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Renderiza a UI de fallback personalizada
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className='flex min-h-screen items-center justify-center bg-gray-50 dark:bg-zinc-900'>
          <div className='mx-auto w-full max-w-[90%]'>
            <div className='text-center'>
              <div className='mb-6 flex justify-center'>
                <Logo
                  width={120}
                  height={120}
                />
              </div>

              <div className='rounded-lg border border-gray-200 bg-white p-6 shadow-lg dark:border-zinc-700 dark:bg-zinc-800'>
                <div className='mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20'>
                  <svg
                    className='size-6 text-red-600 dark:text-red-400'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'>
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z'
                    />
                  </svg>
                </div>

                <h1 className='mb-2 text-xl font-semibold text-gray-900 dark:text-zinc-100'>Ops! Algo deu errado</h1>

                <p className='mb-6 text-gray-600 dark:text-zinc-400'>Ocorreu um erro inesperado na aplicação.</p>

                <div className='flex gap-3'>
                  <Button
                    onClick={this.handleReset}
                    className='flex-1'>
                    Tentar novamente
                  </Button>

                  <Button
                    onClick={this.handleReload}
                    variant='outline'
                    className='flex-1'>
                    Recarregar página
                  </Button>
                </div>

                {/* Exibe detalhes do erro apenas em desenvolvimento */}
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <details
                    className='mt-6 text-left'
                    open>
                    <summary className='cursor-pointer text-sm text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-zinc-300'>
                      Detalhes do erro (desenvolvimento)
                    </summary>
                    <div className='mt-2 max-h-60 overflow-auto rounded bg-gray-100 p-3 font-mono text-xs dark:bg-zinc-700'>
                      <div className='mb-2 font-semibold text-red-600 dark:text-red-400'>
                        {this.state.error.name}: {this.state.error.message}
                      </div>
                      {this.state.error.stack && (
                        <pre className='whitespace-pre-wrap text-gray-700 dark:text-zinc-300'>
                          {this.state.error.stack}
                        </pre>
                      )}
                      {this.state.errorInfo && (
                        <div className='mt-3 border-t border-gray-300 pt-3 dark:border-zinc-600'>
                          <div className='mb-1 font-semibold text-gray-600 dark:text-zinc-400'>Component Stack:</div>
                          <pre className='whitespace-pre-wrap text-gray-700 dark:text-zinc-300'>
                            {this.state.errorInfo.componentStack}
                          </pre>
                        </div>
                      )}
                    </div>
                  </details>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
